import type { ReactNode } from "react";
import type { Permission, User } from "@astro/types";
import { useAuth } from "@astro/auth";

/**
 * RBAC check: does the user hold this permission?
 *
 * NOTE: This is advisory — it drives UX only. The API is the real enforcement
 * boundary. Never grant access based on the client check alone.
 */
export function hasPermission(user: User | null, permission: Permission): boolean {
  return !!user?.permissions.includes(permission);
}

/** ABAC overlay: attribute/row-level rule, e.g. ownership. */
export function can<T>(
  user: User | null,
  permission: Permission,
  resource?: T,
  policy?: (user: User, resource: T) => boolean
): boolean {
  if (!user) return false;
  if (!hasPermission(user, permission)) return false;
  if (policy && resource !== undefined) return policy(user, resource);
  return true;
}

/** Hook form for imperative checks. */
export function usePermission(permission: Permission): boolean {
  const { user } = useAuth();
  return hasPermission(user, permission);
}

/** Declarative gate: renders children only if permitted. */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const allowed = usePermission(permission);
  return <>{allowed ? children : fallback}</>;
}
